import React from 'react';
import { motion, HTMLMotionProps } from 'motion/react';
import { Navigate, Await as _Await, useAsyncValue } from 'react-router-dom';

export const Await = <T extends Record<string, Response | unknown>>({
  resolve,
  children
}: {
  resolve?: Promise<T>;
  children: React.ReactNode | ((data: T) => React.ReactNode);
}) => {
  return (
    <_Await resolve={resolve}>
      <AwaitInner>{children}</AwaitInner>
    </_Await>
  );
};

const AwaitInner = <T extends Record<string, Response | unknown>>({
  children
}: {
  children: React.ReactNode | ((data: T) => React.ReactNode);
}) => {
  const data = useAsyncValue() as T | undefined;

  // Resolved data needs to handle the case where a redirect is returned
  const redirect = Object.values(data || {}).find(
    (v): v is Response => v instanceof Response && v.status === 302
  );

  if (redirect) {
    return <Navigate to={redirect.headers.get('Location') || '/signin'} />;
  }

  return typeof children === 'function' ? (
    <TransitionDiv>{children(data!)}</TransitionDiv>
  ) : (
    <TransitionDiv>{children}</TransitionDiv>
  );
};

export const TransitionDiv = ({
  children,
  ...props
}: React.PropsWithChildren<HTMLMotionProps<'div'>>) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.1, ease: 'easeInOut' }}
    {...props}
  >
    {children}
  </motion.div>
);
